/* 상단 도구 막대 — 선택한 요소 종류에 맞는 도구 (글꼴·크기·색·굵게·정렬·간격 / 선 / 도형 / 사진 모양) + 색·슬라이더 팝오버 */
import { IcPen } from './icons';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Button, Counter, Divider, FilterChip, Temp_ColorInput, Temp_RangeInput, Temp_TileButton } from '@indipia/ui-core';
import { BASE_COLORS, DEFAULT_TEXT_COLOR, PHOTO_CLIPS, SHAPES, fontOf } from '../model/constants';
import type { El } from '../model/types';
import { change, duplicateSelected, ed, rememberUndo, selectedElm, useEd } from '../store/editor';
import { ROT_ICON } from './Artboard';
import { shapeIcon } from './render';
import { chipRow, clipSample, cx, elTile, hint, popTitle, popover, vDivider } from './ui';

const BAR = 'flex min-h-09 shrink-0 items-center gap-xs overflow-x-auto border-b border-border bg-surface-raised px-04 py-02';
const ICO = 'w-[var(--button-height-sm)] px-0 [&_svg]:size-[var(--icon-md)]';
const SW = 'size-06 rounded-sm hover:outline hover:outline-2 hover:outline-offset-1 hover:outline-group';
const SWATCHES = 'grid grid-cols-8 gap-02';

const ICON: Record<string, ReactNode> = {
  left: <svg viewBox="0 0 20 20"><path d="M3 5h14M3 9h9M3 13h14M3 17h9" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" /></svg>,
  center: <svg viewBox="0 0 20 20"><path d="M3 5h14M5.5 9h9M3 13h14M5.5 17h9" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" /></svg>,
  right: <svg viewBox="0 0 20 20"><path d="M3 5h14M8 9h9M3 13h14M8 17h9" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" /></svg>,
  spacing: <svg viewBox="0 0 20 20"><path d="M4 3v14M16 3v14M8 10h4M8 10l1.5-1.5M8 10l1.5 1.5M12 10l-1.5-1.5M12 10l-1.5 1.5" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" /></svg>,
  opacity: <svg viewBox="0 0 20 20"><rect x="3" y="3" width="14" height="14" rx="2" fill="none" stroke="currentColor" strokeWidth="2" /><path d="M3 17L17 3V17Z" fill="currentColor" opacity=".45" /></svg>,
  border: <svg viewBox="0 0 20 20"><rect x="3.5" y="3.5" width="13" height="13" rx="2" fill="none" stroke="currentColor" strokeWidth="2" strokeDasharray="3 2" /></svg>,
  corner: <svg viewBox="0 0 20 20"><path d="M4 16V9a5 5 0 0 1 5-5h7" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" /></svg>,
};
const HEX6 = /^#[0-9a-f]{6}$/i;
function documentColors() {
  const set = new Set<string>();
  ed.pages.forEach((p) => {
    if (p.bg && HEX6.test(p.bg)) set.add(p.bg.toUpperCase());
    p.elements.forEach((e) => [e.color, e.bg, e.stroke].forEach((c) => { if (c && HEX6.test(c)) set.add(c.toUpperCase()); }));
  });
  return [...set].slice(0, 12);
}
/** 글을 고치던 중 막대를 누르면 — 지금까지 친 글을 먼저 요소에 반영 */
function syncEditingText(elm: El) {
  if (elm.type !== 'text') return;
  const body = document.querySelector<HTMLElement>(`[data-showcase] .page-el[data-id="${elm.id}"] .el-text-body`);
  if (body && document.activeElement === body) elm.text = body.innerText;
}

interface Pop { kind: string; key?: string; left: number; top: number }


const clampSize = (n: number) => Math.max(8, Math.min(160, n));
/** 글자 크기 한 칸 — 작을 땐 1, 20부터 2, 40부터 4씩 */
const sizeStep = (fs: number, up: boolean) => (up ? (fs >= 40 ? 4 : fs >= 20 ? 2 : 1) : -(fs > 40 ? 4 : fs > 20 ? 2 : 1));
const DEFAULT_LINE_COLOR = '#1F2937'; // dq-ignore: 문서 선 기본 색(render.tsx 와 같아야 함)
const DEFAULT_PICK = '#3B49CA'; // dq-ignore: 색 선택기 초기값(문서 색)

export function ContextBar({ onFonts, onMarkup }: { onFonts: () => void; onMarkup: (elm: El) => void }) {
  useEd();
  const elm = selectedElm();
  const [pop, setPop] = useState<Pop | null>(null);
  useEffect(() => { setPop(null); }, [elm?.id]);

  if (!elm) return <div className={BAR} data-context-bar role="toolbar" aria-label="요소 도구"><span className={cx(hint, 'px-02')}>요소를 선택하면 글꼴·색·모양을 바꿀 수 있어요</span></div>;
  const open = (ev: React.MouseEvent, kind: string, key?: string) => {
    syncEditingText(elm);
    if (pop && pop.kind === kind && pop.key === key) { setPop(null); return; }
    const r = (ev.currentTarget as HTMLElement).getBoundingClientRect();
    setPop({ kind, key, left: Math.min(window.innerWidth - 310, Math.max(8, r.left)), top: r.bottom + 6 });
  };
  const act = (fn: () => void) => { syncEditingText(elm); fn(); };
  const isOpen = (kind: string, key?: string) => !!pop && pop.kind === kind && pop.key === key;
  /** 도구 버튼 — 켜고 끄는 버튼은 pressed, 팝오버를 여는 버튼은 popup */
  const tool = (p: { label: string; children: ReactNode; onClick: (ev: React.MouseEvent) => void; pressed?: boolean; popup?: [string, string?]; className?: string; showLabel?: boolean }) => (
    <Button size="sm" variant="ghost" className={cx('min-w-[var(--button-height-sm)] whitespace-nowrap px-03', p.pressed ? 'bg-group-subtle text-group-onSubtle' : 'text-text', p.className)}
      title={p.label} aria-label={p.showLabel ? undefined : p.label} aria-pressed={p.pressed}
      aria-haspopup={p.popup ? 'dialog' : undefined} aria-expanded={p.popup ? isOpen(...p.popup) : undefined} onClick={p.onClick}>
      {p.children}
    </Button>
  );
  const colorBtn = (key: 'color' | 'bg' | 'stroke', color: string | undefined, label: string) => {
    const c = color && color !== 'transparent' ? color : null;
    return tool({
      label, popup: ['color', key], className: 'flex-col justify-center gap-xs px-03', onClick: (ev) => open(ev, 'color', key),
      children: (<>
        <span className="text-caption leading-none text-text-secondary [&>b]:text-heading-sm [&>b]:text-text">{key === 'color' ? <b>A</b> : label}</span>
        {/* 지금 색 견본(문서 색) — 없으면 빗금 */}
        <i className={cx('block h-02 w-06 rounded-xs border border-[var(--alpha-black-200)]', !c && 'bg-[repeating-linear-gradient(45deg,var(--gray-0)_0_var(--spacing-02),var(--gray-200)_var(--spacing-02)_var(--spacing-03))]')} style={c ? { background: c } : undefined} />
      </>),
    });
  };
  const caret = <span className="ml-02 text-caption text-text-secondary" aria-hidden>▾</span>;
  const f = fontOf(elm.font);
  const sep = <Divider className={vDivider} role="separator" aria-orientation="vertical" />;
  const fs = Math.round((elm.fontSize || 16) * 10) / 10;
  return (
    // 글을 편집하던 중이어도 포커스를 뺏지 않게
    <div className={BAR} data-context-bar role="toolbar" aria-label="요소 도구" onMouseDown={(ev) => { if ((ev.target as HTMLElement).tagName !== 'INPUT') ev.preventDefault(); }}>
      {elm.type === 'text' && (<>
        <Button size="sm" variant="etc" className="min-w-[calc(var(--spacing-13)-var(--spacing-02))] justify-between font-normal" title="글꼴 바꾸기" onClick={() => act(onFonts)}>
          <span style={{ fontFamily: f.css }}>{f.name}</span>{caret}
        </Button>
        <Counter size="sm" aria-label="글자 크기" value={fs} min={1} max={160}
          onChange={(v) => act(() => change(elm, 'size', (x) => {
            const now = x.fontSize || 16;
            // −/＋ 는 한 칸씩 오니 크기에 맞는 폭으로 바꾸고, 직접 친 값은 그대로
            x.fontSize = v === Math.round(now * 10) / 10 + 1 ? clampSize(Math.round(now + sizeStep(now, true)))
              : v === Math.round(now * 10) / 10 - 1 ? clampSize(Math.round(now + sizeStep(now, false)))
              : Math.max(1, Math.min(160, v)); // 치는 중(1→18)에도 막지 않는다
          }))} />
        {colorBtn('color', elm.color || DEFAULT_TEXT_COLOR, '글자 색')}
        {sep}
        {tool({ label: '굵게', pressed: !!elm.bold, onClick: () => act(() => change(elm, 'bold', (x) => (x.bold = !x.bold))), children: <b>B</b> })}
        {tool({ label: '기울임', pressed: !!elm.italic, onClick: () => act(() => change(elm, 'italic', (x) => (x.italic = !x.italic))), children: <i className="font-serif">I</i> })}
        {tool({ label: '밑줄', pressed: !!elm.underline, onClick: () => act(() => change(elm, 'underline', (x) => (x.underline = !x.underline))), children: <u>U</u> })}
        {tool({ label: `정렬: ${({ left: '왼쪽', center: '가운데', right: '오른쪽' } as const)[elm.align || 'left']}`, className: ICO,
          onClick: () => act(() => change(elm, 'align', (x) => (x.align = ({ left: 'center', center: 'right', right: 'left' } as const)[x.align || 'left']))), children: ICON[elm.align || 'left'] })}
        {tool({ label: '자간·행간', className: ICO, popup: ['spacing'], onClick: (ev) => open(ev, 'spacing'), children: ICON.spacing })}
        {sep}
        {colorBtn('bg', elm.bg, '배경')}
      </>)}
      {elm.type === 'shape' && elm.kind === 'line' && (<>
        {colorBtn('stroke', elm.stroke || DEFAULT_LINE_COLOR, '선 색')}
        {tool({ label: '선 두께', showLabel: true, popup: ['lineWidth'], onClick: (ev) => open(ev, 'lineWidth'), children: <>두께 {elm.sw || 3}{caret}</> })}
        {tool({ label: '선 모양', showLabel: true, popup: ['dash'], onClick: (ev) => open(ev, 'dash'), children: <>{({ solid: '실선', dashed: '점선', dotted: '동그란 점선' } as Record<string, string>)[elm.dash || 'solid']}{caret}</> })}
        {tool({ label: '화살표', showLabel: true, popup: ['arrow'], onClick: (ev) => open(ev, 'arrow'), children: <>{({ none: '화살표 없음', end: '끝 화살표', both: '양쪽 화살표' } as Record<string, string>)[elm.arrow || 'none']}{caret}</> })}
      </>)}
      {elm.type === 'shape' && elm.kind !== 'line' && (<>
        {tool({ label: '모양 바꾸기', className: 'px-02 [&_svg]:size-[var(--icon-lg)]', popup: ['shape'], onClick: (ev) => open(ev, 'shape'), children: <>{shapeIcon(elm.kind || 'rect', elm.bg)}{caret}</> })}
        {colorBtn('bg', elm.bg, '채우기')}
        {tool({ label: '테두리', className: ICO, popup: ['border'], onClick: (ev) => open(ev, 'border'), children: ICON.border })}
        {(!elm.kind || elm.kind === 'rect' || elm.kind === 'round') && tool({ label: '모서리 둥글기', className: ICO, popup: ['corner'], onClick: (ev) => open(ev, 'corner'), children: ICON.corner })}
      </>)}
      {elm.type === 'image' && (<>
        {tool({ label: '사진 모양', showLabel: true, popup: ['clip'], onClick: (ev) => open(ev, 'clip'), children: <>사진 모양: {PHOTO_CLIPS[elm.clip || 'none'].label}{caret}</> })}
        {(!elm.clip || elm.clip === 'none' || elm.clip === 'round') && tool({ label: '모서리 둥글기', className: ICO, popup: ['corner'], onClick: (ev) => open(ev, 'corner'), children: ICON.corner })}
        {tool({ label: '사진 위에 핀·펜으로 고칠 곳을 표시하고 AI로 고치기', showLabel: true, onClick: () => onMarkup(elm), children: <><IcPen size={16} />표시하고 고치기</> })}
      </>)}
      {tool({ label: '투명도', className: ICO, popup: ['opacity'], onClick: (ev) => open(ev, 'opacity'), children: ICON.opacity })}
      {tool({ label: '회전·반전', showLabel: true, popup: ['rotate'], onClick: (ev) => open(ev, 'rotate'), children: <><span className="inline-flex [&_svg]:size-[var(--icon-sm)]">{ROT_ICON}</span>{elm.rot ? `${Math.round(elm.rot)}°` : '회전'}</> })}
      <span className="flex-1" />
      {tool({ label: '복제 (Ctrl+D)', showLabel: true, onClick: () => act(duplicateSelected), children: '복제' })}
      {pop && <Popover pop={pop} elm={elm} onClose={() => setPop(null)} />}
    </div>
  );
}

function Popover({ pop, elm, onClose }: { pop: Pop; elm: El; onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const down = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      if (ref.current && !ref.current.contains(t) && !t.closest('[data-context-bar]')) onClose();
    };
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', down);
    document.addEventListener('keydown', key);
    return () => { document.removeEventListener('mousedown', down); document.removeEventListener('keydown', key); };
  }, [onClose]);
  const key = (pop.key || 'color') as 'color' | 'bg' | 'stroke';
  const set = (k: string, v: string | number, close = true) => {
    change(elm, k, (x) => {
      (x as unknown as Record<string, unknown>)[k] = v;
      if (k === 'sw' && x.kind === 'line') x.h = Math.max(24, (v as number) * 4);
      if (k === 'clip' && v === 'circle' && Math.abs(x.w - x.h) > 4) { const m = Math.min(x.w, x.h); x.w = m; x.h = m; }
    });
    if (close) onClose();
  };
  const slide = (k: string, v: number) => change(elm, k, (x) => {
    if (k === 'ls') x.ls = v ? v.toFixed(2) + 'em' : undefined;
    else if (k === 'lh') x.lh = v;
    else if (k === 'opacity') x.opacity = v >= 100 ? undefined : v / 100;
    else if (k === 'radius') x.radius = v;
    else if (k === 'rot') x.rot = v || undefined;
  });
  const norm = (d: number) => { d = (((d + 180) % 360) + 360) % 360 - 180; return Math.round(d * 10) / 10; };
  const swatch = (c: string, onPick: () => void) => (
    // 견본 색 = 문서 색 데이터
    <Temp_TileButton key={c} bordered className={SW} style={{ background: c }} title={c} aria-label={`색 ${c}`} onClick={onPick} />
  );
  const choices = (items: [string | number, string][], cur: string | number, onPick: (v: string | number) => void, col = false) => (
    <div className={col ? 'flex flex-col items-stretch gap-sm' : chipRow} role="radiogroup">
      {items.map(([v, l]) => <FilterChip key={v} role="radio" aria-checked={cur === v} active={cur === v} onClick={() => onPick(v)}>{l}</FilterChip>)}
    </div>
  );
  let title = '';
  let body: ReactNode = null;
  if (pop.kind === 'color') {
    const doc = documentColors();
    const cur = elm[key];
    title = '색';
    body = (<>
      {!!doc.length && (<><div className={popTitle}>문서에 쓴 색</div>
        <div className={SWATCHES}>{doc.map((c) => swatch(c, () => change(elm, key, (x) => (x[key] = c))))}</div></>)}
      <div className={popTitle}>기본 색</div>
      <div className={SWATCHES}>
        {key === 'bg' && <Temp_TileButton bordered className={cx(SW, 'bg-[linear-gradient(135deg,var(--gray-0)_45%,var(--status-danger-500)_45%,var(--status-danger-500)_55%,var(--gray-0)_55%)]')} title="없음" aria-label="색 없음" onClick={() => change(elm, key, (x) => { delete x[key]; })} />}
        {BASE_COLORS.map((c) => swatch(c, () => change(elm, key, (x) => (x[key] = c))))}
      </div>
      <label className="flex items-center justify-between text-caption text-text-secondary">직접 고르기
        <Temp_ColorInput className="h-07 w-09" label="색 직접 고르기" value={cur && HEX6.test(cur) ? cur : DEFAULT_PICK} onChange={(v) => change(elm, key, (x) => (x[key] = v))} />
      </label>
    </>);
  } else if (pop.kind === 'spacing') {
    title = '자간·행간';
    body = (<>
      <Temp_RangeInput label="글자 간격" min={-0.1} max={0.5} step={0.01} value={parseFloat(elm.ls || '0') || 0} display={(parseFloat(elm.ls || '0') || 0).toFixed(2) + 'em'} onChange={(v) => slide('ls', v)} />
      <Temp_RangeInput label="줄 간격" min={1} max={2.4} step={0.05} value={parseFloat(String(elm.lh || 1.45))} display={parseFloat(String(elm.lh || 1.45)).toFixed(2)} onChange={(v) => slide('lh', v)} />
    </>);
  } else if (pop.kind === 'opacity') {
    const v = Math.round((elm.opacity ?? 1) * 100);
    title = '투명도';
    body = <Temp_RangeInput label="투명도" min={10} max={100} step={1} value={v} display={v + '%'} onChange={(x) => slide('opacity', x)} />;
  } else if (pop.kind === 'corner') {
    const v = elm.radius ?? (elm.kind === 'round' ? 24 : elm.clip === 'round' ? 28 : 0);
    title = '모서리 둥글기';
    body = <Temp_RangeInput label="모서리 둥글기" min={0} max={160} step={1} value={v} display={v + 'px'} onChange={(x) => slide('radius', x)} />;
  } else if (pop.kind === 'border') {
    title = '테두리';
    body = (<>
      <div className={popTitle}>테두리 두께</div>
      {choices([0, 1, 2, 4, 8].map((n) => [n, n ? n + 'px' : '없음']), elm.sw || 0, (v) => set('sw', v, false))}
      <div className={popTitle}>테두리 색</div>
      <div className={SWATCHES}>{BASE_COLORS.slice(0, 16).map((c) => swatch(c, () => change(elm, 'stroke', (x) => { x.stroke = c; if (!x.sw) x.sw = 2; })))}</div>
    </>);
  } else if (pop.kind === 'lineWidth') {
    title = '선 두께';
    body = choices([1, 2, 3, 5, 8, 12].map((n) => [n, n + 'px']), elm.sw || 3, (v) => set('sw', v));
  } else if (pop.kind === 'dash') {
    title = '선 모양';
    body = choices([['solid', '실선'], ['dashed', '점선'], ['dotted', '동그란 점선']], elm.dash || 'solid', (v) => set('dash', v), true);
  } else if (pop.kind === 'arrow') {
    title = '화살표';
    body = choices([['none', '화살표 없음'], ['end', '끝 화살표'], ['both', '양쪽 화살표']], elm.arrow || 'none', (v) => set('arrow', v), true);
  } else if (pop.kind === 'shape') {
    title = '모양';
    body = <div className="grid grid-cols-4 gap-sm">{Object.entries(SHAPES).filter(([k]) => k !== 'line').map(([k, s]) => <Temp_TileButton key={k} title={s.label} aria-label={s.label} selected={(elm.kind || 'rect') === k} className={elTile((elm.kind || 'rect') === k)} onClick={() => set('kind', k)}>{shapeIcon(k, 'var(--gray-500)')}</Temp_TileButton>)}</div>;
  } else if (pop.kind === 'clip') {
    title = '사진 모양';
    body = <div className="grid grid-cols-4 gap-sm">{Object.entries(PHOTO_CLIPS).map(([k, c]) => <Temp_TileButton key={k} title={c.label} aria-label={c.label} selected={(elm.clip || 'none') === k} className={elTile((elm.clip || 'none') === k)} onClick={() => set('clip', k)}><i className={clipSample(k)} /></Temp_TileButton>)}</div>;
  } else if (pop.kind === 'rotate') {
    const r = Math.round(elm.rot || 0);
    const rot = (d: number) => { rememberUndo(elm.id + ':rot'); elm.rot = norm(d) || undefined; ed.bump(); };
    title = '회전·반전';
    body = (<>
      <Temp_RangeInput label="회전" min={-180} max={180} step={1} value={r} display={r + '°'} onChange={(v) => slide('rot', v)} />
      <div className={chipRow}>
        <Button size="sm" variant="etc" onClick={() => rot((elm.rot || 0) - 90)}>↺ 90°</Button>
        <Button size="sm" variant="etc" onClick={() => rot((elm.rot || 0) + 90)}>↻ 90°</Button>
        <Button size="sm" variant="etc" onClick={() => rot(0)}>0°로</Button>
      </div>
      {elm.type !== 'text' && (<>
        <div className={popTitle}>반전</div>
        <div className={chipRow}>
          <FilterChip active={!!elm.flipX} aria-pressed={!!elm.flipX} onClick={() => change(elm, 'flipX', (x) => (x.flipX = !x.flipX || undefined))}>⇋ 좌우 반전</FilterChip>
          <FilterChip active={!!elm.flipY} aria-pressed={!!elm.flipY} onClick={() => change(elm, 'flipY', (x) => (x.flipY = !x.flipY || undefined))}>⇵ 상하 반전</FilterChip>
        </div>
      </>)}
    </>);
  }
  return (
    <div ref={ref} className={cx(popover, 'w-[calc(var(--spacing-13)+var(--spacing-12)+var(--spacing-04))]')} role="dialog" aria-label={title} style={{ left: pop.left, top: pop.top }}
      onMouseDown={(e) => { if ((e.target as HTMLElement).tagName !== 'INPUT') e.preventDefault(); }}>
      {body}
    </div>
  );
}
